import { FetchState, getValue } from "../Models/Client/FetchState"
import { Result } from "../Models/Utility/Result"
import { useCallback, useMemo } from "react"
import {
    SuperAILabelerModel,
    SuperAILabelerParams
} from "../Models/Client/SuperAILabelerModel"
import { useNeqlContextProvider } from "./useNeqlContextProvider"
import { measurer } from "neql"

type Model = {
    items: SuperAILabelerModel[]
    count: number
    total: number
}

export function useSuperAILabelers(
    appId: string,
    pageSize: number,
    params: SuperAILabelerParams
): [FetchState<Model>, () => Promise<unknown>, () => Promise<unknown>] {
    const client = useNeqlContextProvider("measurer")
    const { data, error, loading, fetchMore, refetch } =
        measurer.useLabelerStatsListByAppIdQuery({
            variables: { ...params, limit: pageSize, offset: 0, appId },
            client
        })

    const fetchState = useMemo<FetchState<Model>>(() => {
        if (data === undefined) {
            if (loading) {
                return "loading"
            }

            if (error !== undefined) {
                return Result.error(error.message)
            }

            return undefined
        }

        return Result.ok({
            items: data.hasura_worker_score.map<SuperAILabelerModel>(
                labeler => ({
                    uuid: labeler.worker.uuid,
                    email:
                        labeler.worker.email ??
                        `Email not registered (id: ${labeler.worker.uuid})`,
                    isActive: labeler.app?.active ?? false,
                    taskCompleted: labeler.n_tasks ?? undefined,
                    taskMedianTime: labeler.median_duration_sec ?? undefined,
                    reviewScore: labeler.review_score ?? undefined,
                    reviewedCount: labeler.n_reviewed ?? 0,
                    trustScore: labeler.trust_score ?? undefined,
                    lastLabeled: labeler.worker.updated_at ?? undefined
                })
            ),
            count: data.filteredAggregate.aggregate?.count ?? 0,
            total: data.aggregate.aggregate?.count ?? 0
        })
    }, [data, error, loading])
    const handleFetchMore = useCallback(
        () =>
            fetchMore({
                variables: { offset: getValue(fetchState)?.items.length ?? 0 }
            }),
        [fetchMore, fetchState]
    )

    return [fetchState, handleFetchMore, refetch]
}
